/**
 * navigation/linking.ts
 * Platform identity emails link back to the catalog SPA root with a
 * `?mode=…&token=…` query (see skplatform-api product registry). These are
 * query-based deep links (not path segments), so we parse them explicitly and
 * hand the root navigator the matching auth route — this keeps the existing
 * email-link contract intact with zero backend changes.
 */

import { Platform } from 'react-native';
import type { AuthStackParamList } from './types';

export type AuthDeepLink =
  | { route: 'resetPassword'; params: { token: string } }
  | { route: 'verifyEmail'; params: { token: string } }
  | { route: 'forgotPassword'; params: undefined }
  | { route: 'checkEmail'; params: { email: string } }
  | { route: 'invitationAccept'; params: { token: string } }
  | null;

/** Read `?mode=…&token=…` from the SPA root URL (web only). */
export function readAuthDeepLink(): AuthDeepLink {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const token = params.get('token') ?? '';
  if (mode === 'reset') return { route: 'resetPassword', params: { token } };
  if (mode === 'verify') return { route: 'verifyEmail', params: { token } };
  if (mode === 'forgot') return { route: 'forgotPassword', params: undefined };
  // Post-signup "check your inbox" screen. It has no session yet (or only an
  // unverified one), so without a URL a reload falls through to the boot probe
  // and bounces to login/dashboard. Encoding it as a deep link keeps the user
  // on this screen across refreshes (deep links win over the session probe).
  if (mode === 'check-email') return { route: 'checkEmail', params: { email: params.get('email') ?? '' } };
  // Product invite emails (crm/cal/people/queue/… — every redirect-handoff
  // product) land here. `email` / `org_name` are also on the URL for a
  // same-tab fallback, but the screen re-fetches both fresh via the preview
  // endpoint rather than trusting a possibly-stale query param.
  if (mode === 'accept-invite') return { route: 'invitationAccept', params: { token } };
  // Voice-product auth links use PATH routing (per the product registry), not
  // `?mode=`. A platform/voice-scoped signup (no product handoff) emails a
  // verify link to `/org-auth/business-info?token=…`; map it to the same
  // verify screen so it lands on Step 2 like the `?mode=verify` links do.
  if (token && window.location.pathname.includes('/org-auth/business-info')) {
    return { route: 'verifyEmail', params: { token } };
  }
  if (token && window.location.pathname.includes('/auth/reset-password')) {
    return { route: 'resetPassword', params: { token } };
  }
  return null;
}

/**
 * Signup intent from a product "Start free" CTA (`?mode=signup`). This is NOT a
 * deep link — it must NOT bypass the session probe. It only chooses the
 * UNAUTHENTICATED entry screen (signup instead of the default login); an existing
 * session still wins and the user is sent to the dashboard / product.
 */
export function readSignupIntent(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('mode') === 'signup';
}

/**
 * Pin the SPA URL to the post-signup check-email screen so a reload reopens it
 * (web only). Mirrors the `?mode=…` contract used by the platform email links.
 */
export function setCheckEmailDeepLink(email: string): void {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const q = `?mode=check-email&email=${encodeURIComponent(email)}`;
    window.history.replaceState({}, '', `${window.location.pathname}${q}`);
  }
}

/**
 * Drop the `?mode/?token` query after a deep-linked flow finishes, so a reload
 * doesn't re-open it. Call right before navigating back to login.
 */
export function clearAuthDeepLink(): void {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.history.replaceState({}, '', window.location.pathname);
  }
}

/** Narrowing helper so callers don't repeat the keyof cast. */
export type AuthRouteName = keyof AuthStackParamList;
